import { useMutation, useQueryClient } from '@tanstack/react-query';
import { put } from '@truefit/http-utils';
import { useSnackbar } from 'notistack';
import { GameModel } from '../types';
import { AxiosError } from 'axios';

// Define the type for game update
export interface UpdateGameDetails {
    status?: 'recorded' | 'live' | 'scheduled' | 'delayed';
    startTimestamp?: string;
    endTimestamp?: string;
    enableTracking?: boolean;
    trackingId?: string | null;
    trackingOffset?: string;
    enableAudio?: boolean;
    audioOffset?: string;
    streamId?: string;
    homeTeamColor?: string;
    awayTeamColor?: string;
    available?: boolean;
    version?: number;
    uniqueId: string;
    gameCameras?: Array<{
        uniqueId: string;
        reingestRequired?: boolean;
        reingestStreamId?: string | null;
    }>;
}


const useUpdateGameDetails = () => {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: async (model: UpdateGameDetails) => {
            return await put<GameModel>(`/v1.0/games/${model.uniqueId}`, model);
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['Games'] }),
        onError: (error: AxiosError<any>) => {
            if (error.response?.status === 409) {
                enqueueSnackbar(
                    'Game was modified by another user. Please reload and try again.',
                    { variant: 'warning', autoHideDuration: 6000 }
                );
            } else {
                enqueueSnackbar(
                    error.response?.data?.message || 'Failed to update game',
                    { variant: 'error' }
                );
            }
            return error;
        },
    });
};

export default useUpdateGameDetails;
